import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Save, Send, CheckCircle2, MessageSquareText, RotateCcw, Trash2, ExternalLink, Shuffle, Images } from "lucide-react";
import { CoverPicker } from "@/components/admin/CoverPicker";
import { MD_COMPONENTS } from "@/pages/InsightDetail";
import {
  AdminGate, StatusPill, BUSY_STATUSES, fmtDay, inputCls, FieldLabel, minPublishDay, MIN_REVIEW_DAYS,
} from "@/components/admin/InsightShared";

const CATEGORIES = ["Operations", "Playbook", "Field Notes", "Industry Insight", "AI Fundamentals", "AI Architecture"];
const EDIT_KEYS = ["title", "subtitle", "category", "read_minutes", "excerpt", "body", "cover_image", "slug"];

export default function InsightDraftEditor() {
  const { id } = useParams();
  return (
    <AdminGate returnTo={`/admin/insights/${id}`}>
      <EditorPage id={id} />
    </AdminGate>
  );
}

const toForm = (d) => ({
  ...Object.fromEntries(EDIT_KEYS.map((k) => [k, d[k] ?? ""])),
  publish_at: d.publish_at ? d.publish_at.slice(0, 10) : "",
});

function EditorPage({ id }) {
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async (syncForm = true) => {
    try {
      const { data } = await api.get(`/admin/insight-drafts/${id}`);
      setDraft(data);
      if (syncForm) { setForm(toForm(data)); setDirty(false); }
    } catch {
      toast.error("Draft not found");
      navigate("/admin/insights");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!draft || !BUSY_STATUSES.includes(draft.status)) return undefined;
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [draft, load]);

  if (!draft || !form) {
    return <div className="min-h-[60vh] flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }

  const locked = BUSY_STATUSES.includes(draft.status) || ["published", "scheduled"].includes(draft.status);
  const update = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setDirty(true); };

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      if (data?.id) { setDraft(data); setForm(toForm(data)); setDirty(false); }
      if (okMsg) toast.success(okMsg);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = () => run("save", () => api.patch(`/admin/insight-drafts/${id}`, {
    ...form, read_minutes: Number(form.read_minutes) || 8, publish_at: form.publish_at || null,
  }), "Draft saved");

  const saveIfDirty = async () => (dirty ? !!(await save()) : true);

  const submit = async () => { if (await saveIfDirty()) run("submit", () => api.post(`/admin/insight-drafts/${id}/submit`), "Resubmitted, review email sent"); };
  const approve = async () => {
    if (!(await saveIfDirty())) return;
    const data = await run("approve", () => api.post(`/admin/insight-drafts/${id}/approve`));
    if (data) toast.success(data.status === "scheduled" ? `Approved, goes live ${fmtDay(data.publish_at)}` : "Approved and published");
  };
  const requestChanges = async () => {
    if (notes.trim().length < 3) { toast.error("Add notes for the AI writer."); return; }
    const data = await run("changes", () => api.post(`/admin/insight-drafts/${id}/request-changes`, { notes: notes.trim() }), "Notes sent. AI is revising.");
    if (data) setNotes("");
  };
  const newCover = async () => { if (await saveIfDirty()) run("cover", () => api.post(`/admin/insight-drafts/${id}/cover`), "Picked a new stock photo"); };
  const regenerate = () => run("regen", () => api.post(`/admin/insight-drafts/${id}/regenerate`), "Regenerating draft");
  const discard = async () => {
    if (!window.confirm("Discard this draft?")) return;
    if (await run("delete", () => api.delete(`/admin/insight-drafts/${id}`), "Draft discarded")) navigate("/admin/insights");
  };

  return (
    <div className="bg-white min-h-screen" data-testid="admin-insight-editor">
      <Helmet>
        <title>{`${draft.title || "Draft"} · Insight Composer`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin/insights" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-foreground/55 hover:text-[#ac62ea] mb-6" data-testid="admin-insight-editor-back">
          <ArrowLeft className="h-3.5 w-3.5" /> All drafts
        </Link>
        <EditorHeader draft={draft} />
        {BUSY_STATUSES.includes(draft.status) && <BusyBanner status={draft.status} />}
        {draft.status === "in_review" && draft.error && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900" data-testid="admin-insight-editor-revision-error">
            The last AI revision didn&apos;t finish, so this is still the previous version. You can edit it yourself or request changes again. Details: {draft.error}
          </div>
        )}
        {draft.status === "failed" && (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800 flex items-center justify-between gap-4" data-testid="admin-insight-editor-error">
            <span>The AI writer failed: {draft.error || "unknown error"}</span>
            <Button size="sm" variant="outline" onClick={regenerate} disabled={!!busy} data-testid="admin-insight-editor-regenerate">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Try again
            </Button>
          </div>
        )}

        <div className="mt-8 grid lg:grid-cols-2 gap-8">
          <FormColumn form={form} update={update} locked={locked} minDate={minPublishDay(draft.created_at)} onNewCover={newCover} coverBusy={busy === "cover"} />
          <PreviewColumn form={form} />
        </div>

        {!locked && draft.status !== "failed" && (
          <ReviewBar
            notes={notes} setNotes={setNotes} busy={busy} dirty={dirty}
            onSave={save} onSubmit={submit} onApprove={approve} onRequestChanges={requestChanges} onDiscard={discard}
            publishAt={form.publish_at}
          />
        )}
        <ReviewHistory history={draft.review_history || []} />
      </div>
    </div>
  );
}

function EditorHeader({ draft }) {
  return (
    <div className="flex items-start justify-between gap-6 flex-wrap">
      <div className="min-w-0">
        <div className="label-mono">Insight draft · {draft.topic}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.03em] mt-3 max-w-3xl" data-testid="admin-insight-editor-heading">
          {draft.title || "Drafting…"}
        </h1>
        <div className="mt-3 text-sm text-muted-foreground">
          Publish date: <span data-testid="admin-insight-editor-publish-date">{fmtDay(draft.publish_at)}</span> · created by {draft.created_by}
          {draft.source_url && (
            <> · <a href={draft.source_url} target="_blank" rel="noopener noreferrer" className="text-[#ac62ea] hover:underline">source</a></>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {draft.published_slug && (
          <a href={`/insights/${draft.published_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#ac62ea] hover:underline" data-testid="admin-insight-editor-live-link">
            View post <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        <StatusPill status={draft.status} testId="admin-insight-editor-status" />
      </div>
    </div>
  );
}

const BusyBanner = ({ status }) => (
  <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50 px-5 py-4 text-sm text-sky-800 flex items-center gap-3" data-testid="admin-insight-editor-busy">
    <Loader2 className="h-4 w-4 animate-spin" />
    {status === "revising" ? "The AI writer is applying your notes." : "The AI writer is drafting this post."} This takes about a minute. You'll get a review email when it's ready.
  </div>
);

function FormColumn({ form, update, locked, minDate, onNewCover, coverBusy }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const field = (k, label, props = {}) => (
    <label className="grid gap-1.5">
      <FieldLabel>{label}</FieldLabel>
      <input className={inputCls} value={form[k]} onChange={update(k)} disabled={locked} data-testid={`admin-insight-editor-${k}`} {...props} />
    </label>
  );
  return (
    <div className="grid gap-4 content-start">
      {field("title", "Title")}
      <label className="grid gap-1.5">
        <FieldLabel>Subtitle</FieldLabel>
        <textarea className={inputCls} rows={2} value={form.subtitle} onChange={update("subtitle")} disabled={locked} data-testid="admin-insight-editor-subtitle" />
      </label>
      <div className="grid grid-cols-3 gap-3">
        <label className="grid gap-1.5">
          <FieldLabel>Category</FieldLabel>
          <select className={inputCls} value={form.category} onChange={update("category")} disabled={locked} data-testid="admin-insight-editor-category">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        {field("read_minutes", "Read min", { type: "number", min: 1, max: 30 })}
        {field("publish_at", `Publish (min +${MIN_REVIEW_DAYS}d)`, { type: "date", min: minDate, required: true })}
      </div>
      {field("slug", "URL slug")}
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between">
          <FieldLabel>Cover image</FieldLabel>
          {!locked && (
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1 text-xs text-[#ac62ea] hover:underline" data-testid="admin-insight-editor-browse-covers">
                <Images className="h-3.5 w-3.5" /> Browse library
              </button>
              <button type="button" onClick={onNewCover} disabled={coverBusy} className="inline-flex items-center gap-1 text-xs text-[#ac62ea] hover:underline disabled:opacity-50" data-testid="admin-insight-editor-new-cover">
                {coverBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shuffle className="h-3.5 w-3.5" />}
                {coverBusy ? "Picking…" : "Auto-pick another"}
              </button>
            </div>
          )}
        </div>
        <input className={inputCls} value={form.cover_image} onChange={update("cover_image")} disabled={locked} data-testid="admin-insight-editor-cover_image" />
        <CoverPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          current={form.cover_image}
          onPick={(url) => update("cover_image")({ target: { value: url } })}
        />
      </div>
      <label className="grid gap-1.5">
        <FieldLabel>Excerpt (listing card)</FieldLabel>
        <textarea className={inputCls} rows={3} value={form.excerpt} onChange={update("excerpt")} disabled={locked} data-testid="admin-insight-editor-excerpt" />
      </label>
      <label className="grid gap-1.5">
        <FieldLabel>Body (Markdown)</FieldLabel>
        <textarea className={`${inputCls} font-mono text-[13px] leading-relaxed`} rows={28} value={form.body} onChange={update("body")} disabled={locked} data-testid="admin-insight-editor-body" />
      </label>
    </div>
  );
}

const PreviewColumn = ({ form }) => (
  <div className="lg:sticky lg:top-20 self-start rounded-2xl bg-[#0b1220] border border-white/10 overflow-hidden" data-testid="admin-insight-editor-preview">
    <div className="px-6 py-3 border-b border-white/10 text-[11px] font-mono uppercase tracking-[0.16em] text-white/50">Live preview</div>
    <div className="max-h-[78vh] overflow-y-auto px-8 py-8">
      {form.cover_image && <img src={form.cover_image} alt="" className="w-full aspect-[3/2] object-cover rounded-xl mb-6" />}
      <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#b967ff]">{form.category} · {form.read_minutes} min read</div>
      <h1 className="font-display text-3xl font-semibold tracking-[-0.03em] text-white mt-3">{form.title || "Untitled"}</h1>
      <p className="text-white/60 mt-3 leading-relaxed">{form.subtitle}</p>
      <div className="mt-8"><ReactMarkdown components={MD_COMPONENTS}>{form.body || ""}</ReactMarkdown></div>
    </div>
  </div>
);

function ReviewBar({ notes, setNotes, busy, dirty, onSave, onSubmit, onApprove, onRequestChanges, onDiscard, publishAt }) {
  const future = publishAt && new Date(`${publishAt}T23:59:59Z`) > new Date();
  return (
    <div className="mt-10 rounded-2xl border border-border p-6 grid gap-5" data-testid="admin-insight-editor-review-bar">
      <label className="grid gap-1.5">
        <FieldLabel>Notes for the AI writer</FieldLabel>
        <textarea className={inputCls} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Tighten the intro, add a stat on tool sprawl, make the takeaway less salesy…" data-testid="admin-insight-editor-notes" />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={onRequestChanges} disabled={!!busy} data-testid="admin-insight-editor-request-changes">
          {busy === "changes" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <MessageSquareText className="h-4 w-4 mr-1.5" />} Request changes
        </Button>
        <Button variant="outline" onClick={onSave} disabled={!!busy || !dirty} data-testid="admin-insight-editor-save">
          <Save className="h-4 w-4 mr-1.5" /> Save edits
        </Button>
        <Button variant="outline" onClick={onSubmit} disabled={!!busy} data-testid="admin-insight-editor-submit">
          <Send className="h-4 w-4 mr-1.5" /> Resubmit for review
        </Button>
        <Button variant="ghost" onClick={onDiscard} disabled={!!busy} className="text-rose-600 hover:text-rose-700" data-testid="admin-insight-editor-discard">
          <Trash2 className="h-4 w-4 mr-1.5" /> Discard
        </Button>
        <div className="flex-1" />
        <Button onClick={onApprove} disabled={!!busy} className="bg-[#ac62ea] hover:bg-[#9851d6] text-white rounded-full px-5" data-testid="admin-insight-editor-approve">
          {busy === "approve" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
          {future ? `Approve & schedule for ${fmtDay(`${publishAt}T14:00:00Z`)}` : "Approve & publish now"}
        </Button>
      </div>
    </div>
  );
}

const ReviewHistory = ({ history }) => (history.length === 0 ? null : (
  <div className="mt-8" data-testid="admin-insight-editor-history">
    <FieldLabel>Review history</FieldLabel>
    <ul className="mt-3 grid gap-3">
      {[...history].reverse().map((h, i) => (
        <li key={h.at || i} className="rounded-xl border border-border px-4 py-3 text-sm">
          <div className="text-xs text-muted-foreground">{h.by} · {new Date(h.at).toLocaleString()}</div>
          <div className="mt-1 whitespace-pre-wrap">{h.notes}</div>
        </li>
      ))}
    </ul>
  </div>
));
